import React, { useContext } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, Paper, Typography } from '@mui/material';

import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { RootState } from '../../../app/rootReducer';
import { FeatureFlags } from '../../../common/constants/featureFlags';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { getFormattedTimestamp } from '../../../common/helpers';
import { INote } from '../interfaces/INote';
import styles from './notesDrawer.module.scss';

interface HistoryColumnProps {
  onRetrieve: (note: INote) => void;
}

export const HistoryColumn: React.FC<HistoryColumnProps> = ({ onRetrieve }) => {
  const notes = useSelector((state: RootState) => state.notes.notes);
  const { flags } = useContext(FeatureFlagsContext);
  const isMibCodebackDraftEnabled =
    flags[FeatureFlags.UW_NORA_MIB_CODEBACK_DRAFT_ENABLED];

  const sorted = [...notes].sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  return (
    <Box className={styles.HistoryColumn}>
      <Box
        px={2}
        pt={1.5}
        pb={1}
        borderBottom="0.5px solid rgba(0,0,0,0.12)"
        flexShrink={0}
      >
        <Typography variant="caption" fontWeight={500} color="text.secondary">
          Submitted notes
        </Typography>
      </Box>
      <Box flex={1} sx={{ overflowY: 'auto', minHeight: 0 }} px={1.5} py={1}>
        {sorted.length === 0 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            textAlign="center"
            py={3}
          >
            No notes submitted yet.
          </Typography>
        ) : (
          sorted.map((note: INote) => (
            <Paper
              key={note.id}
              elevation={0}
              sx={{
                bgcolor: '#f5f5f3',
                borderRadius: '8px',
                px: 1.25,
                py: 1,
                mb: 0.75,
              }}
            >
              <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                gap={0.75}
                mb={0.5}
              >
                <Box display="flex" flexDirection="column">
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    color="text.secondary"
                  >
                    {note.userName}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {getFormattedTimestamp(note.date, 'MMM D · h:mm A')}
                  </Typography>
                </Box>
                {isMibCodebackDraftEnabled && (
                  <Button
                    disableRipple
                    onClick={() => onRetrieve(note)}
                    startIcon={
                      <FontAwesomeIcon icon={faCopy} style={{ fontSize: 10 }} />
                    }
                    sx={{
                      textTransform: 'none',
                      fontSize: 10,
                      fontWeight: 400,
                      color: '#185FA5',
                      bgcolor: 'transparent',
                      border: '0.5px solid #185FA5',
                      borderRadius: '4px',
                      padding: '2px 7px',
                      lineHeight: 1,
                      '& .MuiButton-startIcon': {
                        marginRight: '3px',
                        '& svg': { fontSize: 10 },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(24,95,165,0.08)',
                        borderColor: '#185FA5',
                      },
                    }}
                  >
                    Retrieve
                  </Button>
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.primary"
                sx={{
                  whiteSpace: 'pre-wrap',
                  display: 'block',
                  lineHeight: 1.5,
                }}
              >
                {note.note}
              </Typography>
              {note.mibCodes?.length > 0 && (
                <Box className={styles.MibCodesSection}>
                  <Box className={styles.MibCodesLabel}>
                    MIB Codeback Draft:
                  </Box>
                  <Box className={styles.MibCodesTags}>
                    {note.mibCodes.map(code => (
                      <Box
                        key={code}
                        component="span"
                        className={styles.MibCodeTag}
                      >
                        {code}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          ))
        )}
      </Box>
    </Box>
  );
};
